import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowRight, Lock, Mail, Crown } from "lucide-react";
import { toast } from "sonner";

const goGoogle = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/library";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const formatErr = (d) => {
  if (!d) return "Login failed";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e.msg || JSON.stringify(e)).join(" ");
  if (d.msg) return d.msg;
  return String(d);
};

export default function Login() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setBusy(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      setUser(res.data.user);
      toast.success(`Welcome back, ${res.data.user.name}`);
      nav("/library", { replace: true, state: { user: res.data.user } });
    } catch (e2) {
      toast.error(formatErr(e2?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white text-black" data-testid="login-page">
      {/* Left: brand panel */}
      <div className="relative bg-black text-white grain p-10 md:p-16 flex flex-col justify-between">
        <Logo inverted />
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#E60000] mb-3">Sign in</div>
          <h1 className="font-display text-5xl md:text-7xl font-black tracking-tighter leading-[0.85]">
            The vault<br />awaits the<br /><span className="text-[#E60000]">squad.</span>
          </h1>
          <p className="mt-6 text-white/65 max-w-md">
            Pick how you want to enter — Google for the team, owner credentials
            for the master account.
          </p>
        </div>
        <div className="text-xs text-white/40 font-mono">
          Monster Squad™ · v01 · Built for the people who make the cut.
        </div>
      </div>

      {/* Right: form */}
      <div className="p-8 md:p-16 flex items-center">
        <div className="w-full max-w-md mx-auto">
          <Link to="/" className="text-xs uppercase tracking-[0.2em] font-bold text-black/50 hover:text-[#E60000]" data-testid="back-home">
            ← Back home
          </Link>

          <Tabs defaultValue="google" className="mt-8">
            <TabsList className="rounded-sm h-11 bg-transparent border border-black/15 p-0 w-full grid grid-cols-2">
              <TabsTrigger value="google" data-testid="tab-google" className="rounded-none data-[state=active]:bg-black data-[state=active]:text-white">
                Squad · Google
              </TabsTrigger>
              <TabsTrigger value="owner" data-testid="tab-owner" className="rounded-none data-[state=active]:bg-[#E60000] data-[state=active]:text-white">
                Owner · Email
              </TabsTrigger>
            </TabsList>

            <TabsContent value="google" className="mt-8">
              <h2 className="font-heading font-black text-2xl tracking-tight">Sign in with Google</h2>
              <p className="text-sm text-black/60 mt-1">
                Use your work Google to join the squad.
              </p>
              <Button
                onClick={goGoogle}
                className="mt-6 h-12 w-full rounded-sm bg-black hover:bg-[#E60000] text-white font-medium tracking-tight"
                data-testid="google-signin-button"
              >
                <Mail className="w-4 h-4 mr-2" /> Continue with Google
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-xs text-black/40 mt-4">
                We never store your Google password. Auth is brokered by Emergent.
              </p>
            </TabsContent>

            <TabsContent value="owner" className="mt-8">
              <div className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-[#E60000]" />
                <h2 className="font-heading font-black text-2xl tracking-tight">Owner sign in</h2>
              </div>
              <p className="text-sm text-black/60 mt-1">Reserved for the squad master account.</p>

              <form onSubmit={submit} className="mt-6 space-y-3">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-black/60">Email</label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="MAL@monsterSQ.nas"
                    className="mt-1 rounded-sm h-11"
                    data-testid="owner-email"
                    required
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-black/60">Password</label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••••"
                    className="mt-1 rounded-sm h-11"
                    data-testid="owner-password"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  className="h-12 w-full rounded-sm bg-[#E60000] hover:bg-[#CC0000] text-white font-medium tracking-tight"
                  data-testid="owner-signin-button"
                >
                  <Lock className="w-4 h-4 mr-2" /> {busy ? "Signing in…" : "Sign in as owner"}
                </Button>
              </form>
              <p className="text-xs text-black/40 mt-4">
                The owner account bypasses the paywall and unlocks every premium feature.
              </p>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
